import { Ban } from 'lucide-react';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, useNavigate } from 'react-router-dom';

import { ExternalLinkIcon } from '@/components/icons/external-link';
import { PlaneSendIcon } from '@/components/icons/plane-send';
import { Button } from '@/components/ui/button';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useOpeningDetails } from '@/contexts/opening-context';
import { useNavigationPaths } from '@/hooks/use-navigation-paths';
import {
  BOOST,
  INDEED,
  JOVEO,
  POOL,
  REACH,
  SPRING,
  TALROO,
  ZIPRECRUITER,
} from '@/types/channels';

import { REACH_SUPPORT_URL } from '../constants';

interface CardButtonProps {
  channel: string;
  openingId?: string;
}

export function CardButton({ channel, openingId }: CardButtonProps) {
  const navigate = useNavigate();
  const { postJobPath } = useNavigationPaths();
  const { opening } = useOpeningDetails();
  const { isActive: isOpeningActive, isSourcingEnabled } = opening;
  const { setSelectedChannelSlugs, setSelectedOpeningIds } =
    useJobPostingContext();

  const handlePostJobClick = useCallback(() => {
    if (!openingId) return;

    setSelectedOpeningIds([openingId]);
    setSelectedChannelSlugs([channel]);

    navigate(postJobPath);
  }, [
    channel,
    navigate,
    openingId,
    postJobPath,
    setSelectedChannelSlugs,
    setSelectedOpeningIds,
  ]);

  if (!isOpeningActive || !isSourcingEnabled) {
    return (
      <Button
        variant="default"
        disabled
        className="bg-fx-neutral-100 text-fx-neutral-400 border-0 opacity-60"
        aria-label="Opening inactive"
      >
        <Ban className="text-fx-neutral-400 size-4 shrink-0" />
        {!isOpeningActive ? (
          <FormattedMessage
            defaultMessage="Opening inactive"
            description="Disabled button: requisition is inactive in Hire"
          />
        ) : (
          <FormattedMessage
            defaultMessage="Sourcing disabled"
            description="Disabled button: sourcing funnel turned off for this job"
          />
        )}
      </Button>
    );
  }

  switch (channel) {
    case INDEED:
    case BOOST:
    case SPRING:
    case JOVEO:
    case TALROO:
    case POOL:
    case ZIPRECRUITER:
      return (
        <Button variant="secondary" onClick={handlePostJobClick}>
          <PlaneSendIcon />
          <FormattedMessage
            defaultMessage="Post Job"
            description="CTA on inactive channel card to start job posting flow"
          />
        </Button>
      );
    case REACH:
      return (
        <Button variant="secondary" asChild>
          <Link to={REACH_SUPPORT_URL} target="_blank">
            <ExternalLinkIcon className="text-fx-primary" />
            <FormattedMessage
              defaultMessage="Learn More"
              description="External link to Reach support or docs from inactive card"
            />
          </Link>
        </Button>
      );
    default:
      return null;
  }
}
